import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DocumentRevisionEntite } from '../entites/historique-document/document-revision';
import { DocumentRevisionService } from '../services/document-revision-service';
import { ETypeLien } from '../entites/enums/type-lien-document';
import { BonTravailEntite } from '../entites/mecanique/bon-travail';
import { EtapeBonTravailEntite } from '../entites/mecanique/etape-bon-travail';

/**
 * Reusable Google Docs-style document history panel with snapshot preview.
 */
@Component({
    selector: 'app-document-historique',
    standalone: true,
    imports: [CommonModule, SidebarModule, ButtonModule, TagModule, DialogModule, ConfirmDialogModule],
    providers: [ConfirmationService],
    template: `
        <p-sidebar [(visible)]="visible" position="right" [style]="{width:'min(28rem, 100vw)'}" (onHide)="visibleChange.emit(false)">
            <ng-template pTemplate="header">
                <div class="flex align-items-center gap-2">
                    <i class="pi pi-history" aria-hidden="true"></i>
                    <span class="font-semibold">Historique</span>
                </div>
            </ng-template>

            @if (loading) {
                <div class="flex justify-content-center p-4">
                    <i class="pi pi-spin pi-spinner text-2xl"></i>
                </div>
            } @else if (!revisions.length) {
                <p class="text-color-secondary px-2">Aucune révision pour le moment.</p>
            } @else {
                <div class="flex flex-column gap-3">
                    @for (rev of revisions; track rev.documentRevisionId) {
                        <div class="p-3 border-1 surface-border border-round">
                            <div class="flex justify-content-between align-items-start gap-2 mb-2">
                                <div>
                                    <div class="font-semibold">
                                        {{ rev.employe?.prenom }} {{ rev.employe?.nom }}
                                    </div>
                                    <div class="text-sm text-color-secondary">
                                        {{ rev.dateDebut | date:'yyyy-MM-dd HH:mm' }}
                                        @if (rev.dateFin && rev.dateFin !== rev.dateDebut) {
                                            – {{ rev.dateFin | date:'HH:mm' }}
                                        }
                                    </div>
                                    @if (rev.label) {
                                        <div class="text-sm mt-1">{{ rev.label }}</div>
                                    }
                                    @if (rev.sealReason) {
                                        <div class="text-sm text-orange-500 mt-1">{{ rev.sealReason }}</div>
                                    }
                                </div>
                                @if (rev.sealed || !rev.hasSnapshot) {
                                    <p-tag severity="secondary" value="Scellé"></p-tag>
                                } @else {
                                    <p-tag severity="success" value="Restaurable"></p-tag>
                                }
                            </div>
                            @if (rev.diffSummary?.length) {
                                <div class="flex flex-wrap gap-1 mb-2">
                                    @for (diff of rev.diffSummary; track diff.path) {
                                        <p-tag severity="info" [value]="diff.label || diff.path" styleClass="text-xs"></p-tag>
                                    }
                                </div>
                            }
                            <div class="flex flex-wrap gap-2">
                                @if (rev.hasSnapshot && !rev.sealed) {
                                    <p-button
                                        label="Aperçu"
                                        icon="pi pi-eye"
                                        styleClass="p-button-sm p-button-outlined"
                                        (onClick)="ouvrirApercu(rev)">
                                    </p-button>
                                }
                                @if (canRestore && rev.hasSnapshot && !rev.sealed) {
                                    <p-button
                                        label="Restaurer"
                                        icon="pi pi-replay"
                                        styleClass="p-button-sm p-button-outlined"
                                        (onClick)="confirmerRestore(rev)">
                                    </p-button>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </p-sidebar>

        <p-dialog
            [(visible)]="apercuVisible"
            [modal]="true"
            [dismissableMask]="true"
            [style]="{ width: 'min(56rem, 96vw)' }"
            [contentStyle]="{ 'max-height': '70vh', overflow: 'auto' }"
            styleClass="document-historique-apercu"
            (onHide)="fermerApercu()">
            <ng-template pTemplate="header">
                <div class="flex flex-column gap-1 pr-3">
                    <div class="flex align-items-center gap-2">
                        <i class="pi pi-eye" aria-hidden="true"></i>
                        <span class="font-semibold">Aperçu de la version</span>
                    </div>
                    @if (apercuRevision) {
                        <div class="text-sm text-color-secondary font-normal">
                            {{ apercuRevision.employe?.prenom }} {{ apercuRevision.employe?.nom }}
                            · {{ apercuRevision.dateDebut | date:'yyyy-MM-dd HH:mm' }}
                            @if (apercuRevision.dateFin && apercuRevision.dateFin !== apercuRevision.dateDebut) {
                                – {{ apercuRevision.dateFin | date:'HH:mm' }}
                            }
                        </div>
                    }
                </div>
            </ng-template>

            @if (apercuLoading) {
                <div class="flex justify-content-center p-5">
                    <i class="pi pi-spin pi-spinner text-2xl"></i>
                </div>
            } @else if (apercuErreur) {
                <p class="text-color-secondary m-0">{{ apercuErreur }}</p>
            } @else if (apercuBonTravail) {
                @if (apercuRevision?.diffSummary?.length) {
                    <div class="flex flex-wrap gap-1 mb-3">
                        @for (diff of apercuRevision.diffSummary; track diff.path) {
                            <p-tag severity="info" [value]="diff.label || diff.path" styleClass="text-xs"></p-tag>
                        }
                    </div>
                }

                <div class="grid formgrid">
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Type</label>
                        <div>{{ afficherType(apercuBonTravail.type) }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Raison</label>
                        <div>{{ apercuBonTravail.raison?.description || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Contre-maître</label>
                        <div>{{ afficherEmploye(apercuBonTravail.contreMaitre) }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Mécanicien</label>
                        <div>{{ afficherEmploye(apercuBonTravail.mecanicien) }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Fournisseur</label>
                        <div>{{ apercuBonTravail.fournisseur?.nom || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Emplacement</label>
                        <div>{{ apercuBonTravail.emplacement?.nom || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Date de travail</label>
                        <div>{{ apercuBonTravail.dateTravail ? (apercuBonTravail.dateTravail | date:'yyyy-MM-dd') : '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Date de fin</label>
                        <div>{{ apercuBonTravail.dateFin ? (apercuBonTravail.dateFin | date:'yyyy-MM-dd') : '—' }}</div>
                    </div>
                    <div class="col-12 field mb-3">
                        <label class="text-sm text-color-secondary block mb-1">Note du bon de travail</label>
                        @if (apercuBonTravail.note) {
                            <div class="p-3 border-1 surface-border border-round surface-ground text-sm white-space-normal"
                                 [innerHTML]="apercuBonTravail.note"></div>
                        } @else {
                            <div>—</div>
                        }
                    </div>
                </div>

                <div class="mt-2">
                    <div class="font-semibold mb-2">Étapes ({{ apercuEtapes.length }})</div>
                    @if (!apercuEtapes.length) {
                        <p class="text-color-secondary text-sm m-0">Aucune étape.</p>
                    } @else {
                        <div class="flex flex-column gap-2">
                            @for (etape of apercuEtapes; track etape.etapeBonTravailId || $index; let i = $index) {
                                <div class="p-3 border-1 surface-border border-round">
                                    <div class="flex justify-content-between align-items-start gap-2 mb-1">
                                        <div class="font-medium">
                                            #{{ etape.ordre ?? (i + 1) }}
                                            {{ etape.description || 'Sans description' }}
                                        </div>
                                        <div class="flex gap-1 flex-wrap justify-content-end">
                                            @if (etape.indComplete) {
                                                <p-tag severity="success" value="Complétée" styleClass="text-xs"></p-tag>
                                            }
                                            @if (etape.indAFaire) {
                                                <p-tag severity="warning" value="À faire" styleClass="text-xs"></p-tag>
                                            }
                                            @if (etape.indElementMajeur) {
                                                <p-tag severity="danger" value="Majeur" styleClass="text-xs"></p-tag>
                                            }
                                        </div>
                                    </div>
                                    <div class="text-sm text-color-secondary">
                                        Mécanicien: {{ afficherEmploye(etape.mecanicien) }}
                                        · Pièces: {{ etape.pieces?.length || 0 }}
                                    </div>
                                    @if (etape.note) {
                                        <div class="text-sm mt-2 white-space-pre-wrap">{{ etape.note }}</div>
                                    }
                                    @if (etape.pieces?.length) {
                                        <ul class="mt-2 mb-0 pl-3 text-sm">
                                            @for (piece of etape.pieces; track piece.pieceBonTravailId || $index) {
                                                <li>
                                                    {{ piece.item?.description || piece.item?.numero || 'Pièce' }}
                                                    @if (piece.quantite) {
                                                        <span class="text-color-secondary"> × {{ piece.quantite }}</span>
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            } @else {
                <p class="text-color-secondary m-0">Impossible d'afficher l'aperçu de cette version.</p>
            }

            <ng-template pTemplate="footer">
                <div class="flex justify-content-end gap-2 flex-wrap">
                    <p-button
                        label="Fermer"
                        icon="pi pi-times"
                        styleClass="p-button-text"
                        (onClick)="fermerApercu()">
                    </p-button>
                    @if (canRestore && apercuRevision?.hasSnapshot && !apercuRevision?.sealed) {
                        <p-button
                            label="Restaurer cette version"
                            icon="pi pi-replay"
                            (onClick)="confirmerRestore(apercuRevision!)">
                        </p-button>
                    }
                </div>
            </ng-template>
        </p-dialog>

        <p-confirmDialog></p-confirmDialog>
    `
})
export class DocumentHistoriqueComponent implements OnChanges {
    @Input() visible = false;
    @Input() typeLien: ETypeLien = ETypeLien.BonTravail;
    @Input() entityId: number;
    @Input() canRestore = true;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() restored = new EventEmitter<void>();

    revisions: DocumentRevisionEntite[] = [];
    loading = false;

    apercuVisible = false;
    apercuLoading = false;
    apercuErreur: string | null = null;
    apercuRevision: DocumentRevisionEntite | null = null;
    apercuBonTravail: BonTravailEntite | null = null;

    constructor(
        private documentRevisionService: DocumentRevisionService,
        private confirmationService: ConfirmationService
    ) {}

    get apercuEtapes(): EtapeBonTravailEntite[] {
        const etapes = this.apercuBonTravail?.etapes ?? [];
        return [...etapes].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['visible'] || changes['entityId']) && this.visible && this.entityId) {
            this.charger();
        }
    }

    charger(): void {
        this.loading = true;
        this.documentRevisionService.obtenirHistorique(this.typeLien, this.entityId).subscribe({
            next: (revs) => {
                this.revisions = revs ?? [];
                this.loading = false;
            },
            error: () => {
                this.revisions = [];
                this.loading = false;
            }
        });
    }

    ouvrirApercu(rev: DocumentRevisionEntite): void {
        this.apercuVisible = true;
        this.apercuLoading = true;
        this.apercuErreur = null;
        this.apercuRevision = rev;
        this.apercuBonTravail = null;

        this.documentRevisionService.obtenirRevision(rev.documentRevisionId).subscribe({
            next: (detail) => {
                this.apercuRevision = detail;
                this.apercuBonTravail = this.parserSnapshot(detail);
                this.apercuLoading = false;
                if (!this.apercuBonTravail) {
                    this.apercuErreur = detail.hasSnapshot
                        ? 'Instantané invalide ou non pris en charge pour cet aperçu.'
                        : 'Aucun instantané disponible pour cette révision.';
                }
            },
            error: () => {
                this.apercuLoading = false;
                this.apercuErreur = 'Impossible de charger l\'aperçu.';
            }
        });
    }

    fermerApercu(): void {
        this.apercuVisible = false;
        this.apercuLoading = false;
        this.apercuErreur = null;
        this.apercuRevision = null;
        this.apercuBonTravail = null;
    }

    confirmerRestore(rev: DocumentRevisionEntite): void {
        this.confirmationService.confirm({
            message: 'Restaurer cette version ? Les champs du document seront remplacés.',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Restaurer',
            rejectLabel: 'Annuler',
            accept: () => {
                this.documentRevisionService.restaurer(rev.documentRevisionId).subscribe({
                    next: () => {
                        this.fermerApercu();
                        this.restored.emit();
                        this.charger();
                    }
                });
            }
        });
    }

    afficherEmploye(employe?: { prenom?: string; nom?: string } | null): string {
        if (!employe) {
            return '—';
        }
        const nom = `${employe.prenom || ''} ${employe.nom || ''}`.trim();
        return nom || '—';
    }

    afficherType(type?: number | null): string {
        if (type === 1) {
            return 'Interne';
        }
        if (type === 2) {
            return 'Externe';
        }
        return type != null ? String(type) : '—';
    }

    private parserSnapshot(detail: DocumentRevisionEntite): BonTravailEntite | null {
        if (!detail?.snapshotJson || this.typeLien !== ETypeLien.BonTravail) {
            return null;
        }
        try {
            return JSON.parse(detail.snapshotJson) as BonTravailEntite;
        } catch {
            return null;
        }
    }
}
